import { type ElementType, type InputHTMLAttributes, type Ref } from "react";
import { type InputBaseComponentProps } from "@mui/material";
import { IMaskInput } from "react-imask";

import { TextField } from "ui/text-field/text-field";
import { type TextFieldProps } from "ui/text-field/text-field.types";

type MaskedInputSlotProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "onChange" | "value" | "ref"
> & {
  mask: string;
  value?: string;
  ref?: Ref<HTMLInputElement>;
  /**
   * `InputBase`'s own handler, which it calls on every keystroke. `IMaskInput` reports through
   * `onAccept` with the masked string rather than an event, so it is invoked with the one shape
   * `InputBase` reads back — `event.target.value`.
   */
  onChange?: (event: { target: { name: string; value: string } }) => void;
};

/**
 * Module scope, never inlined or built in a render: `inputComponent` is an element type, so a fresh
 * identity on each render remounts the `<input>` and the field loses focus mid-typing.
 *
 * `inputRef`, not `ref` — `IMaskInput`'s `ref` points at its own component instance, and only
 * `inputRef` reaches the DOM node that `react-hook-form` focuses on a validation error.
 */
const MaskedInputSlot = ({ mask, name, onChange, ref, ...rest }: MaskedInputSlotProps) => (
  <IMaskInput
    {...rest}
    name={name}
    mask={mask}
    inputRef={ref}
    onAccept={(value: string) => onChange?.({ target: { name: name ?? "", value } })}
  />
);

export type MaskedTextFieldProps = Omit<
  TextFieldProps,
  "onChange" | "inputComponent" | "inputProps" | "multiline" | "rows" | "type"
> & {
  /** An [imask](https://imask.js.org) pattern, e.g. `"00/00/0000"`. */
  mask: string;
  /** The masked string, not an event — a mask reports through `onAccept`, which carries no event. */
  onChange?: (value: string) => void;
};

export const MaskedTextField = ({ mask, onChange, ...rest }: MaskedTextFieldProps) => (
  <TextField
    {...rest}
    // MUI types the slot as `ElementType<InputBaseComponentProps>`, and that type cannot describe
    // this one: its `[arbitrary: string]: any` index signature does not satisfy a required `mask`,
    // and its `onChange` is a DOM handler where a mask needs a value callback. The cast is on the
    // component reference only, so `MaskedInputSlotProps` above stays fully checked.
    inputComponent={MaskedInputSlot as ElementType<InputBaseComponentProps>}
    inputProps={{ mask }}
    onChange={(event) => onChange?.(event.target.value)}
  />
);
